<template>
  <b-modal
    id="history-modal"
    size="lg"
    :title="this.$translate('History')"
    hide-footer
    centered
    content-class="rounded-8"
    @hidden="clear"
    @show="loadHistory"
  >
    <div class="row">
      <div class="col-12 col-lg-12 col-sm-12 col-xs-12" v-if="loading">
        <Loader />
      </div>
      <div class="col-12 col-lg-12 col-sm-12 col-xs-12" v-else>
        <Table v-if="!selectedMilesone" :columns="columns" :rows="history" tableName="historyTable" />
        <div v-else>
          <div class="header-actions">
            <b-button size="sm" variant="outline-primary" @click="clear">{{ 'back' | i18n }}</b-button>
            <b-button size="sm" variant="primary" @click="resetToVersion">{{ 'reset_to_this_version' | i18n }}</b-button>
          </div>
          <Group
            v-for="group in selectedTemplate.groups"
            :key="group.id"
            :group="group"
            :setFieldValue="() => {}"
            :setFieldTextInputValue="() => {}"
            :fieldsValue="fieldsValue"
            :fieldsTextInputValue="fieldsTextInputValue"
            :getFile="getFile"
            :getFileURL="getFileURL"
            :confirmationValues="confirmationValues"
            :enableConfirmation="true"
            :hasAccessToProject="false"
            :hasAccessToExternal="false"
            :setConfirmationValue="() => {}"
            :feeds="feeds"
            groupName="Topic"
          />
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import axios from 'axios';

import Loader from '../../../../common/loader';
import Table from '../../../../common/table';
import Notification from '../../../../common/notification';
import Group from '../../../../reports/cases/components/group';
import { ENTRY_TYPES, VALIDATION_RULES } from '../../../../../../../../../constants';
import { AuthMixin, DeleteRowMixin } from '../../../../../../mixins';
import api from '../../../../../api';

export default {
  title: 'History modal',
  mixins: [AuthMixin, DeleteRowMixin],
  components: {
    Table,
    Group,
    Loader,
  },
  data() {
    return {
      loading: false,
      history: [],
      selectedTemplate: {},
      fieldsValue: {},
      fieldsTextInputValue: {},
      confirmationValues: {},
      feeds: {},
      selectedMilesone: null,
      columns: [
        {
          field: 'version',
          title: this.$translate('Version'),
          key: '1',
          align: 'left',
        },
        {
          field: 'createdAt',
          title: this.$translate('date_created'),
          key: '2',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return moment.utc(row.createdAt).local().format('YYYY-MM-DD HH:mm');
          },
        },
        {
          field: 'user',
          title: this.$translate('created_by'),
          key: '3',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return row.user ? row.user.username : '';
          },
        },
        {
          field: 'status',
          title: this.$translate('Status'),
          key: '4',
          align: 'left',
          width: 85,

          renderBodyCell: ({ row }) => {
            return row.sent ? <div class="sent">{this.$translate('sent')}</div> : '';
          },
        },
        {
          field: 'actions',
          title: '',
          key: '6',
          width: 50,
          renderBodyCell: ({ row }) => {
            return (
              <div class="action-block">
                <div class="copy-button" on-click={() => this.generateHistoryPDF(row)}>
                  {this.$translate(row.pdf ? 'PDF' : 'Generate PDF')}
                </div>
                <div class="copy-button" on-click={() => this.goToHistoryDetails(row)}>
                  {this.$translate('Details')}
                </div>
              </div>
            );
          },
        },
      ],
    };
  },
  props: {
    project: {
      type: Object,
      default: () => {},
    },
  },
  methods: {
    clear() {
      this.selectedMilesone = null;
      this.selectedTemplate = {};
      this.fieldsValue = {};
      this.fieldsTextInputValue = {};
      this.confirmationValues = {};
      this.feeds = {};
    },
    async loadHistory() {
      this.history = [];
      const history = await api.projectMilestones.getHistoryById(this.project.id);
      this.history = history.data;
    },
    async generateHistoryPDF(row) {
      if (row.pdf) {
        const auth = this.authStorageService.getAuth();
        const url = `${axios.defaults.baseURL}/api/v1/one/project-milestone-history/${row.id}/pdf?authorization=${auth.access_token_link}`;
        return window.open(url, '_blank');
      }

      try {
        this.loading = true;
        await api.projectMilestones.generateHistoryPDF(row.id);
        await this.loadHistory();
        this.loading = false;
      } catch (e) {
        this.loading = false;
      }
    },
    getFile(id) {
      return window.open(this.getFileURL(id), '_blank');
    },
    getFileURL(id) {
      const auth = this.authStorageService.getAuth();
      return `${axios.defaults.baseURL}/api/v1/one/project-milestone-history/${this.selectedMilesone.id}/file/${id}?authorization=${auth.access_token_link}`;
    },
    async resetToVersionConfirmed() {
      try {
        await api.projectMilestones.resetHistory(this.selectedMilesone.milestone, this.selectedMilesone.id);
        this.$root.$emit('bv::hide::modal', `history-modal`);
        Notification({ type: 'success', message: this.$translate('success') });
      } catch (err) {
        Notification({ type: 'error', message: err.message });
      }
    },
    resetToVersion() {
      this.deleteSelectedRows(
        {},
        async () => {
          this.resetToVersionConfirmed();
        },
        false,
        this.$translate('reset_milestone_version_msg')
      );
    },
    goToHistoryDetails(data) {
      this.selectedMilesone = data;

      const template = this.selectedMilesone.template;
      const confirmation = this.selectedMilesone.confirmation;

      const groups = [];
      const groupsFields = {};

      const translation = template.translation;

      let language = '';

      try {
        const auth = localStorage.getItem('auth');
        const lng = localStorage.getItem('lng');

        if (lng) {
          const str = JSON.parse(lng);
          language = str.toUpperCase();
        }

        if (auth) {
          const json = JSON.parse(auth);
          if (json.language) {
            language = json.language;
          }
        }
      } catch (e) {
        language = '';
      }

      template.fields.map((f) => {
        if (f.showTextInput) {
          Vue.set(this.fieldsTextInputValue, f.key, f.textInputValue);
          Vue.set(this.fieldsTextInputValue, f.id, f.textInputValue);
        }

        if (f.entryType === ENTRY_TYPES.FEED) {
          this.$set(this.feeds, this.selectedMilesone.fields[f.key], []);
        }

        if (!f.editByMobile && f.entryType !== ENTRY_TYPES.FEED) {
          return;
        }
        if (!groupsFields[f.groupId]) {
          groupsFields[f.groupId] = [];
        }
        f.required = f.rules.some((r) => r.name === VALIDATION_RULES.REQUIRED);
        groupsFields[f.groupId].push({
          ...f,
          title: translation[f.key] && translation[f.key][language] ? translation[f.key][language] : f.title,
        });
      });

      template.groups.map((g) => {
        const data = {
          name: translation[g.id] && translation[g.id][language] ? translation[g.id][language] : g.name,
          id: g.id,
          isConfirmation: g.isConfirmation,
          isFile: g.isFile,
          file: g.file,
          fields: groupsFields[g.id],
        };
        groups.push(data);
      });

      this.fieldsValue = {
        ...this.selectedMilesone.report.fields,
        ...this.selectedMilesone.fields,
      };

      this.selectedTemplate = {
        name: template.name,
        id: template.id,
        groups,
      };

      template.groups.forEach((g) => {
        if (!confirmation[g.id]) {
          this.$set(this.confirmationValues, g.id, false);
        } else {
          this.$set(this.confirmationValues, g.id, confirmation[g.id]);
        }

        template.fields.forEach((field) => {
          if (!confirmation[field.key]) {
            this.$set(this.confirmationValues, field.key, false);
          } else {
            this.$set(this.confirmationValues, field.key, confirmation[field.key]);
          }
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .copy-button {
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  color: rgb(49, 141, 255);
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  cursor: pointer;
  margin-right: 5px;
  white-space: nowrap;
}

.header-actions {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
}

.sent {
  color: white;
  background: linear-gradient(#2260ff, #2260ff);
  border-radius: 4px;
  border: none;
  text-align: center;
  padding: 0 10px;
}

::v-deep .modal-header {
  border: none;
  padding-bottom: 0px !important;
}

.action-block {
  display: flex;
  justify-content: flex-end;
}

::v-deep .rounded-8 {
  border-radius: 8px;
}
</style>
